import { Resource, usePagination } from "@imminently/react/store/query";
import { Card } from "@imminently/react/ui/core/card";
import { Text } from "@imminently/react/ui/core/text";
import { Button, Stack } from "@mui/material";
import capitalize from "lodash/capitalize";
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { getLoadEncounterRoute } from "./load-encounter";
import { PersonIcon } from "@/assets/icons";
import { Icon } from "@/components/Icon";
import global, { calculateAgeMoment } from "@/global";
import { Patient } from "@/resources/types";
import { patientService } from "@/services";
import { css, royalblue } from "@/theme";

export const getSearchRoute = (query: string) => `/search?query=${query}`;

const styles = {
  header: css`
    background-color: ${royalblue[25]};
  `,
  body: css`
    background-color: ${royalblue[50]};
    padding-bottom: 1.5rem;
  `,
  card: css`
    padding: 1.5rem;
    min-width: 34rem;
    border-radius: 0.5rem;
    border: 1px solid ${royalblue[100]};
    background-color: ${royalblue[25]};
  `,
  results: css`
    > * {
      border-bottom: 1px solid ${royalblue[100]};
    }
  `,
  item: css`
    padding: 0.5rem;
    justify-content: space-between;
    align-items: flex-start;
    cursor: pointer;

    &:hover {
      background-color: ${royalblue[50]};
    }
  `,
};

const DOT = "•";

const PatientItem = ({ patient }: { patient: Patient }) => {
  const { firstName, lastName, sex, dateOfBirth, umrn } = patient;

  const fullName = [lastName.toUpperCase(), capitalize(firstName)].join(", ");
  const age = dateOfBirth ? calculateAgeMoment(dateOfBirth) : "-";
  const sexAge = `${sex ? sex[0].toUpperCase() : "-"} (${age})`;

  return (
    <Stack gap={0.5}>
      <Text variant="h5">{fullName}</Text>
      <Stack direction="row" gap={1}>
        <Text variant="body2">{sexAge}</Text>
        {umrn && (
          <>
            <Text variant="body2">{DOT}</Text>
            <Text variant="body2">{umrn}</Text>
          </>
        )}
      </Stack>
    </Stack>
  );
};

type ResultCardProps<T extends Resource> = {
  title: string;
  icon: React.ReactNode;
  data: T[];
  renderItem: (item: T) => React.ReactNode;
  loadMore: (() => void) | null;
  selectItem: (id: string) => void;
};

const ResultCard = <T extends Resource>({
  title,
  icon,
  data,
  renderItem,
  loadMore,
  selectItem,
}: ResultCardProps<T>) => {
  return (
    <Card css={styles.card} elevation={0}>
      <Stack gap={2}>
        <Stack direction="row" alignItems="center" gap={1}>
          {icon}
          <Text variant="h5" bold sx={{ textTransform: "capitalize" }}>
            {title}
          </Text>
        </Stack>

        <Stack gap={1} css={styles.results}>
          {data.map((d) => (
            <Stack
              key={d.id}
              direction="row"
              css={styles.item}
              onClick={() => selectItem(d.id)}
            >
              {renderItem(d)}
              <Icon icon="arrowright" />
            </Stack>
          ))}
        </Stack>

        {loadMore && (
          <Stack direction="row" justifyContent="end">
            <Button variant="text" onClick={loadMore}>
              Show more
            </Button>
          </Stack>
        )}
      </Stack>
    </Card>
  );
};

const renderPatient = (p: Patient) => <PatientItem patient={p} />;

export const SearchPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams({ query: "" });
  const query = searchParams.get("query") || "";

  const { pagination, loadMore, setPerPage } = usePagination(0, 5);
  const { data, refetch } = patientService.useGetList({
    keepPreviousData: true,
    meta: {
      pagination,
      search: `*${query}*`,
      // sort by last modified as they are more likely relevant
      sort: {
        field: "lastModified",
        order: "desc",
      },
    },
  });

  useEffect(() => {
    setPerPage(5);
    refetch();

    // This is to fix a bug where old data hangs around
    global.queryClient.removeQueries("patients");
  }, [query]);

  const patients = data?.data || [];
  const total = data?.total || 0;

  const selectPatient = (id: string) => {
    const route = getLoadEncounterRoute(id);
    navigate(route);
  };

  return (
    <Stack flex={1}>
      <Stack
        css={styles.header}
        pt={6}
        pb={12}
        justifyContent="center"
        alignItems="center"
      >
        <Text variant="h1" mt={4}>
          {`Showing ${total} results for "${query}"`}
        </Text>
      </Stack>
      <Stack css={styles.body} pt={3} flex={1} gap={1} alignItems="center">
        <ResultCard
          title="Patients"
          icon={<PersonIcon />}
          renderItem={renderPatient}
          data={patients}
          loadMore={patients.length < total ? loadMore : null}
          selectItem={selectPatient}
        />
      </Stack>
    </Stack>
  );
};
